import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import {
  OUTREACH_CHANNEL_LABEL,
  OUTREACH_CHANNELS,
  OUTREACH_OUTCOME_LABEL,
  onPlatform,
  type OutreachChannel,
  type OutreachFeedbackOutcome,
  type OutreachFeedbackSentiment,
} from '@/client/types/sales';
import { cn } from '@/lib/utils';
import { SalesPageShell } from './components/SalesPageShell';
import { ConversationThread, threadItems } from './components/ConversationThread';
import { Segmented, btnGhost, btnPrimary, selectCls, toastSales } from './components/sales-ui';
import { SALES_OUTREACH_TEMPLATES } from './fixtures';
import {
  useComposeOutreachMutation,
  useLogOutreachMutation,
  useOutreachFeedbackMutation,
  useSalesClaimsQuery,
  useSalesMeQuery,
  useSalesOutreachQuery,
  useSalesPolicyQuery,
  useSalesRepsQuery,
  useSalesSubjectQuery,
  useSalesSubjectThreadQuery,
  useSalesSubjectsQuery,
  useSendOutreachMutation,
} from '@/client/api/sales/sales';
import { defaultNextStepDue } from './sales-live';
import { promptActAs, useCurrentRep } from './sales-store';

type ListFilter = 'all' | 'due' | 'drafts';

export function RedirectThreadToOutreach() {
  const { subjectId } = useParams<{ subjectId?: string }>();
  return <Navigate to={subjectId ? `/admin/sales/outreach/${subjectId}` : '/admin/sales/outreach'} replace />;
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 16);
}

function fromLocalInput(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export default function SalesOutreachPage() {
  const { subjectId } = useParams<{ subjectId?: string }>();
  const navigate = useNavigate();
  const rep = useCurrentRep();
  const me = useSalesMeQuery();
  const canWrite = Boolean(rep) && me.data?.canWriteOutreach !== false;
  const claimsQ = useSalesClaimsQuery({ limit: 200 });
  const outreachQ = useSalesOutreachQuery({ limit: 200 });
  const subjectsQ = useSalesSubjectsQuery({ limit: 200 });
  const repsQ = useSalesRepsQuery();
  const composeMut = useComposeOutreachMutation();
  const sendMut = useSendOutreachMutation();
  const logMut = useLogOutreachMutation();
  const policyQ = useSalesPolicyQuery();
  const claims = claimsQ.data?.claims ?? [];
  const outreaches = outreachQ.data?.outreach ?? [];
  const subjects = subjectsQ.data?.subjects ?? [];
  const reps = repsQ.data ?? [];
  const subjectById = (id: string) => subjects.find((s) => s.id === id);
  const getRep = (id: string) => reps.find((r) => r.id === id);
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [q, setQ] = useState('');
  const [channelFilter, setChannelFilter] = useState<OutreachChannel | 'all'>('all');
  const [channel, setChannel] = useState<OutreachChannel>('whatsapp');
  const [body, setBody] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [dest, setDest] = useState('/signup');
  const [nextStepDue, setNextStepDue] = useState(() => toLocalInput(defaultNextStepDue()));

  const queue = useMemo(
    () => (rep ? claims.filter((c) => c.ownerId === rep.id && ['claimed', 'in_pursuit', 'committed'].includes(c.status)) : []),
    [rep, claims],
  );
  const due = useMemo(
    () =>
      outreaches.filter(
        (o) =>
          o.nextStepDue &&
          new Date(o.nextStepDue).getTime() <= Date.now() &&
          (!rep || o.actorId === rep.id),
      ),
    [rep, outreaches],
  );
  const dueIds = useMemo(() => new Set(due.map((o) => o.subjectId)), [due]);

  const scoped = useMemo(() => {
    if (!rep) return outreaches;
    const mine = new Set(claims.filter((c) => c.ownerId === rep.id).map((c) => c.subjectId));
    return outreaches.filter((o) => o.actorId === rep.id || mine.has(o.subjectId));
  }, [outreaches, claims, rep]);

  const draftIds = useMemo(
    () => new Set(scoped.filter((o) => o.status === 'draft').map((o) => o.subjectId)),
    [scoped],
  );

  const conversations = useMemo(() => {
    const bySubject = new Map<string, ReturnType<typeof threadItems>>();
    for (const o of scoped) {
      if (o.status === 'blocked') continue;
      const list = bySubject.get(o.subjectId) ?? [];
      list.push(o);
      bySubject.set(o.subjectId, list);
    }
    if (listFilter === 'all') {
      for (const c of queue) {
        if (!bySubject.has(c.subjectId)) bySubject.set(c.subjectId, []);
      }
    }
    const query = q.trim().toLowerCase();
    return [...bySubject.entries()]
      .map(([id, rows]) => {
        const items = threadItems(rows);
        const last = items[items.length - 1];
        const subject = subjectById(id);
        return { id, subject, items, last };
      })
      .filter((c) => {
        if (listFilter === 'due' && !dueIds.has(c.id)) return false;
        if (listFilter === 'drafts' && !draftIds.has(c.id)) return false;
        if (!query) return true;
        const blob = `${c.subject?.displayName ?? ''} ${c.last?.body ?? ''}`.toLowerCase();
        return blob.includes(query);
      })
      .sort((a, b) => (b.last?.at ?? '').localeCompare(a.last?.at ?? ''));
  }, [scoped, queue, q, listFilter, dueIds, draftIds]);

  const selectedId = subjectId && conversations.some((c) => c.id === subjectId) ? subjectId : conversations[0]?.id;
  const subjectQ = useSalesSubjectQuery(selectedId);
  const threadQ = useSalesSubjectThreadQuery(selectedId);
  const conversationsHydrated = useMemo(() => {
    const extra = threadQ.data?.outreach ?? [];
    return conversations.map((c) => {
      const named = c.id === selectedId ? (c.subject ?? subjectQ.data) : c.subject;
      if (c.id !== selectedId || extra.length === 0) return named === c.subject ? c : { ...c, subject: named };
      const byId = new Map(c.items.map((o) => [o.id, o]));
      for (const o of extra) byId.set(o.id, o);
      const items = threadItems([...byId.values()]);
      return { ...c, subject: named, items, last: items[items.length - 1] };
    });
  }, [conversations, selectedId, threadQ.data?.outreach, subjectQ.data]);
  const selected = conversationsHydrated.find((c) => c.id === selectedId);
  const subject = selected?.subject ?? subjectQ.data;
  const onFf = subject ? onPlatform(subject) : false;
  const templates = SALES_OUTREACH_TEMPLATES.filter((t) => t.channel === channel);
  const visibleItems =
    selected && channelFilter !== 'all'
      ? selected.items.filter((o) => o.channel === channelFilter)
      : (selected?.items ?? []);
  const pending = selected?.items
    .slice()
    .reverse()
    .find((o) => o.direction === 'outbound' && !o.feedback && o.status !== 'draft' && o.status !== 'blocked');

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const tpl = SALES_OUTREACH_TEMPLATES.find((t) => t.id === id);
    if (!tpl || !subject) return;
    const area = subject.areas[0]?.region ?? '{area}';
    setBody(tpl.body.replace(/\{name\}/g, subject.displayName).replace(/\{area\}/g, area));
    if (tpl.defaultDest) setDest(tpl.defaultDest);
    setChannel(tpl.channel);
  };

  // With the server flag on, a WhatsApp send really goes out from the
  // platform's number, for prospects on or off FeelyFeely.
  const viaWhatsApp = channel === 'whatsapp' && Boolean(policyQ.data?.whatsappDispatch);

  /** `dispatch` sends through the platform; without it the message is only logged. */
  const sendOutbound = (dispatch: boolean) => {
    if (!subject || !body.trim()) return;
    if (!canWrite) {
      if (!rep) promptActAs('send or log outreach');
      else toast.error('You do not have permission to send outreach.');
      return;
    }
    const dueAt = fromLocalInput(nextStepDue) ?? defaultNextStepDue();
    const ok = () => {
      toast.success(
        viaWhatsApp
          ? 'Sent on WhatsApp'
          : dispatch
            ? 'Recorded on-platform — not delivered to the prospect'
            : 'Logged — not delivered. Copy the message and send it yourself.',
      );
      setBody('');
    };
    if (dispatch) {
      composeMut.mutate(
        {
          subjectId: subject.id,
          channel,
          body,
          templateId: templateId || undefined,
          dest: onFf ? dest : undefined,
          mode: onFf ? 'on_platform' : undefined,
          nextStepDue: dueAt,
        },
        {
          onSuccess: (draft) => sendMut.mutate({ id: draft.id, nextStepDue: dueAt }, { onSuccess: ok, onError: toastSales }),
          onError: toastSales,
        },
      );
    } else {
      logMut.mutate(
        {
          subjectId: subject.id,
          channel,
          body,
          templateId: templateId || undefined,
          nextStepDue: dueAt,
        },
        { onSuccess: ok, onError: toastSales },
      );
    }
  };

  return (
    <SalesPageShell
      title="Outreach"
      description="The conversation with a prospect, any channel. Send, log what you sent, or record a reply."
      crumbs={
        subject
          ? [{ label: 'Outreach', href: '/admin/sales/outreach' }, { label: subject.displayName }]
          : [{ label: 'Outreach' }]
      }
      icon={Send}
      actions={
        <Segmented
          value={listFilter}
          onChange={setListFilter}
          options={[
            { id: 'all', label: 'All' },
            { id: 'due', label: `Due (${due.length})` },
            { id: 'drafts', label: `Drafts (${draftIds.size})` },
          ]}
        />
      }
    >
      <div className="grid min-h-[32rem] overflow-hidden rounded-xl border border-gray-200 bg-white lg:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)]">
        <aside className="flex flex-col border-b border-gray-200 lg:border-b-0 lg:border-r">
          <div className="border-b border-gray-100 p-3">
            <input
              className={`${selectCls} w-full`}
              placeholder="Find a conversation"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Find a conversation"
            />
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto">
            {conversationsHydrated.length === 0 ? (
              <li className="px-4 py-8 text-sm text-gray-400">Nothing in this list.</li>
            ) : (
              conversationsHydrated.map((c) => {
                const active = c.id === selectedId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/sales/outreach/${c.id}`)}
                      className={cn(
                        'w-full border-l-2 px-4 py-3 text-left transition-colors',
                        active ? 'border-slate-900 bg-gray-50' : 'border-transparent hover:bg-gray-50',
                      )}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-medium text-gray-900">
                          {c.subject?.displayName ?? c.id}
                        </span>
                        <span className="shrink-0 text-[11px] text-gray-400">
                          {c.last
                            ? new Date(c.last.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                            : dueIds.has(c.id)
                              ? 'Due'
                              : ''}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-gray-500">
                        {c.last
                          ? `${OUTREACH_CHANNEL_LABEL[c.last.channel]} · ${c.last.body}`
                          : 'No messages yet'}
                      </p>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </aside>

        <section className="flex min-w-0 flex-col">
          {subject ? (
            <>
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                <div>
                  <Link
                    to={`/admin/sales/prospects/${subject.id}`}
                    className="text-sm font-semibold text-gray-900 hover:underline"
                  >
                    {subject.displayName}
                  </Link>
                  <p className="text-xs text-gray-400">
                    {selected!.items.length} {selected!.items.length === 1 ? 'message' : 'messages'}
                    {getRep(selected!.last?.actorId ?? '')
                      ? ` · last logged by ${getRep(selected!.last!.actorId)?.name}`
                      : ''}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-500">
                  Channel
                  <select
                    className={selectCls}
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value as OutreachChannel | 'all')}
                  >
                    <option value="all">All</option>
                    {OUTREACH_CHANNELS.map((id) => (
                      <option key={id} value={id}>
                        {OUTREACH_CHANNEL_LABEL[id]}
                      </option>
                    ))}
                  </select>
                </label>
              </header>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                <ConversationThread
                  items={visibleItems}
                  prospectName={subject.displayName}
                  empty={
                    channelFilter === 'all'
                      ? 'No messages yet. Send or log the first touch below.'
                      : `Nothing on ${OUTREACH_CHANNEL_LABEL[channelFilter]}.`
                  }
                />
                {pending && rep ? <FeedbackRow outreachId={pending.id} /> : null}
              </div>
              <footer className="border-t border-gray-100 p-3">
                {rep ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <select
                        className={selectCls}
                        value={channel}
                        onChange={(e) => setChannel(e.target.value as OutreachChannel)}
                        aria-label="Channel"
                      >
                        {OUTREACH_CHANNELS.map((id) => (
                          <option key={id} value={id}>
                            {OUTREACH_CHANNEL_LABEL[id]}
                          </option>
                        ))}
                      </select>
                      <select
                        className={selectCls}
                        value={templateId}
                        onChange={(e) => applyTemplate(e.target.value)}
                        aria-label="Template"
                      >
                        <option value="">Template</option>
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                      {onFf ? (
                        <input
                          className={`${selectCls} w-40`}
                          value={dest}
                          onChange={(e) => setDest(e.target.value)}
                          aria-label="Tracked dest"
                          placeholder="/signup"
                        />
                      ) : null}
                      <label className="flex items-center gap-2 text-xs text-gray-500">
                        Next step
                        <input
                          type="datetime-local"
                          className={selectCls}
                          value={nextStepDue}
                          onChange={(e) => setNextStepDue(e.target.value)}
                        />
                      </label>
                    </div>
                    <textarea
                      className={`${selectCls} w-full`}
                      rows={3}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder={onFf ? 'In-app copy, or what they said' : 'What you sent, or what they said'}
                    />
                    {subject && !onFf && channel === 'in_app' ? (
                      <p className="text-xs text-amber-800">In-app send needs a FeelyFeely user. Log WhatsApp, phone, or email instead.</p>
                    ) : null}
                    <p className="text-xs text-gray-500">
                      {viaWhatsApp
                        ? 'WhatsApp sends from the platform number. Other actions only log the touch on the floor — they don’t message the prospect.'
                        : 'Logging records the touch on the floor; it does not message the prospect. Reach out on the channel yourself, then mark it sent.'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {onFf ? (
                        <button className={btnPrimary} disabled={!body.trim()} onClick={() => sendOutbound(true)}>
                          {viaWhatsApp ? 'Send on WhatsApp' : 'Send in-app'}
                        </button>
                      ) : (
                        <>
                          <button
                            className={btnGhost}
                            disabled={!body.trim()}
                            onClick={() => {
                              void navigator.clipboard?.writeText(body);
                              toast.success('Copied');
                            }}
                          >
                            Copy
                          </button>
                          {viaWhatsApp ? (
                            <button className={btnPrimary} disabled={!body.trim() || !canWrite} onClick={() => sendOutbound(true)}>
                              Send on WhatsApp
                            </button>
                          ) : null}
                          <button className={viaWhatsApp ? btnGhost : btnPrimary} disabled={!body.trim() || !canWrite} onClick={() => sendOutbound(false)}>
                            Mark sent
                          </button>
                        </>
                      )}
                      <button
                        className={btnGhost}
                        disabled={!body.trim() || !canWrite}
                        onClick={() => {
                          if (!canWrite) return;
                          composeMut.mutate(
                            {
                              subjectId: subject.id,
                              channel,
                              body,
                              templateId: templateId || undefined,
                              dest: onFf ? dest : undefined,
                            },
                            {
                              onSuccess: () => {
                                setBody('');
                                setListFilter('drafts');
                                toast.success('Draft saved');
                              },
                              onError: toastSales,
                            },
                          );
                        }}
                      >
                        Save draft
                      </button>
                      <button
                        className={btnGhost}
                        disabled={!body.trim() || !canWrite}
                        onClick={() => {
                          if (!canWrite) return;
                          logMut.mutate(
                            {
                              subjectId: subject.id,
                              channel,
                              body,
                              direction: 'inbound',
                            },
                            {
                              onSuccess: () => {
                                setBody('');
                                toast.success('Reply logged');
                              },
                              onError: toastSales,
                            },
                          );
                        }}
                      >
                        Log reply
                      </button>
                      <Link to="/admin/sales/rules" className="self-center text-xs text-gray-400 hover:underline">
                        Rules
                      </Link>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                    <button type="button" className="font-medium text-blue-700 hover:underline" onClick={() => promptActAs('send or log outreach')}>
                      Choose who you are acting as
                    </button> 
                    to send, log, or record a reply.
                  </p>
                )}
              </footer>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center px-6 py-16 text-sm text-gray-400">
              Pick a prospect on the left.
            </div>
          )}
        </section>
      </div>
    </SalesPageShell>
  );
}

function FeedbackRow({ outreachId }: { outreachId: string }) {
  const [outcome, setOutcome] = useState<OutreachFeedbackOutcome>('interested');
  const [sentiment, setSentiment] = useState<OutreachFeedbackSentiment>('neutral');
  const feedbackMut = useOutreachFeedbackMutation();
  return (
    <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
      <select className={selectCls} value={sentiment} onChange={(e) => setSentiment(e.target.value as OutreachFeedbackSentiment)}>
        <option value="positive">Positive</option>
        <option value="neutral">Neutral</option>
        <option value="negative">Negative</option>
      </select>
      <select className={selectCls} value={outcome} onChange={(e) => setOutcome(e.target.value as OutreachFeedbackOutcome)}>
        {(Object.keys(OUTREACH_OUTCOME_LABEL) as OutreachFeedbackOutcome[]).map((k) => (
          <option key={k} value={k}>
            {OUTREACH_OUTCOME_LABEL[k]}
          </option>
        ))}
      </select>
      <button
        className={btnGhost}
        onClick={() => {
          feedbackMut.mutate(
            { id: outreachId, sentiment, outcome },
            {
              onSuccess: () => toast.success('Feedback saved'),
              onError: toastSales,
            },
          );
        }}
      >
        Save feedback
      </button>
    </div>
  );
}
